import type { BaseUser } from "@models";
import type { Suggestion } from "@tiptap-pro/extension-tracked-changes";
import type { JSONContent } from "@tiptap/core";
import {
  findTiptapCommentAuthor,
  formatTiptapCollaborationTime,
  formatTiptapCommentAuthorName,
  parseTiptapCollaborationDate,
} from "./tiptapCommentUtils";

type SuggestionWithPreviewText = Suggestion & {
  fullText?: string;
};

type SuggestionUserMetadata = Record<string, unknown>;

const DOVETAIL_AGENT_SUGGESTION_SOURCE = "dovetail-agent";
const DEFAULT_AGENT_NAME = "Dovetail";
const DEFAULT_AGENT_USER_ID = "dovetail";
const DOVETAIL_AGENT_AVATAR_SRC = "/favicon.svg";

export const dedupeTiptapSuggestions = (suggestions: Suggestion[]) => {
  return Array.from(
    new Map(
      suggestions.map((suggestion) => [suggestion.id, suggestion])
    ).values()
  );
};

export const getTiptapSuggestionTypeLabel = (suggestion: Suggestion) => {
  switch (suggestion.type) {
    case "add":
      return "Insertion";
    case "delete":
      return "Deletion";
    case "replace":
      return "Replacement";
    case "markChange":
      return "Formatting";
    case "sink":
      return "Indented";
    case "lift":
      return "Outdented";
    default:
      return "Suggestion";
  }
};

export const truncateTiptapSuggestionText = (value: string, maxLength = 140) => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trimEnd()}...`;
};

const getNodeText = (node: JSONContent): string => {
  const childText =
    node.content?.map(getNodeText).filter(Boolean).join(" ") ?? "";

  return [node.text, childText]
    .filter((value): value is string => typeof value === "string" && !!value)
    .join(" ");
};

export const getTiptapSuggestionPreview = (suggestion: Suggestion) => {
  const suggestionWithPreviewText = suggestion as SuggestionWithPreviewText;
  const nodeText = (suggestion.insertedNodes ?? suggestion.deletedNodes)
    ?.map(getNodeText)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  const text =
    nodeText ||
    suggestionWithPreviewText.fullText?.trim() ||
    suggestion.text?.trim() ||
    suggestion.replacedText?.trim() ||
    "";

  if (text) {
    return truncateTiptapSuggestionText(text);
  }

  const nodeType =
    suggestion.insertedNodes?.[0]?.type ?? suggestion.deletedNodes?.[0]?.type;

  return nodeType ? `${nodeType} block` : "Document block";
};

export const getTiptapSuggestionAuthor = (suggestion: Suggestion) => {
  const metadataName = suggestion.userMetadata?.name;

  return typeof metadataName === "string" && metadataName.trim()
    ? metadataName
    : suggestion.userId;
};

const normalizeUserId = (value: string | null | undefined) => {
  const trimmedValue = value?.trim();

  return trimmedValue || null;
};

const normalizeText = (value: string | null | undefined) => {
  const trimmedValue = value?.trim();

  return trimmedValue || null;
};

const getSuggestionUserMetadata = (
  suggestion: Suggestion
): SuggestionUserMetadata => {
  const metadata = suggestion.userMetadata;

  return metadata && typeof metadata === "object"
    ? (metadata as SuggestionUserMetadata)
    : {};
};

const getSuggestionMetadataText = (
  suggestion: Suggestion,
  key: string
): string | null => {
  const value = getSuggestionUserMetadata(suggestion)[key];

  return typeof value === "string" ? normalizeText(value) : null;
};

export const isDovetailAgentTiptapSuggestion = (suggestion: Suggestion) =>
  getSuggestionMetadataText(suggestion, "actor") ===
    DOVETAIL_AGENT_SUGGESTION_SOURCE ||
  getSuggestionMetadataText(suggestion, "source") ===
    DOVETAIL_AGENT_SUGGESTION_SOURCE ||
  Boolean(getSuggestionMetadataText(suggestion, "agentUserId"));

export const getTiptapSuggestionAuthorDisplay = (
  suggestion: Suggestion,
  orgUsers: BaseUser[]
) => {
  const metadataName = getSuggestionMetadataText(suggestion, "name");
  const isAgentAuthored = isDovetailAgentTiptapSuggestion(suggestion);

  if (isAgentAuthored) {
    const agentName =
      getSuggestionMetadataText(suggestion, "agentName") ??
      metadataName ??
      DEFAULT_AGENT_NAME;
    const agentUserId =
      getSuggestionMetadataText(suggestion, "agentUserId") ??
      DEFAULT_AGENT_USER_ID;
    const suggestionUserId = normalizeUserId(suggestion.userId);
    const onBehalfOfUserId =
      getSuggestionMetadataText(suggestion, "onBehalfOfUserId") ??
      (suggestionUserId &&
      suggestionUserId !== agentUserId &&
      suggestionUserId !== DEFAULT_AGENT_USER_ID
        ? suggestionUserId
        : null);
    const onBehalfOfUser = findTiptapCommentAuthor(orgUsers, onBehalfOfUserId);
    if (onBehalfOfUser) {
      const authorName = formatTiptapCommentAuthorName(onBehalfOfUser);

      return {
        authorName,
        avatarName: authorName,
        avatarSrc: null,
        avatarUserId: onBehalfOfUser.id,
        coAuthorName: agentName,
        isAgentAuthored,
      };
    }

    return {
      authorName: agentName,
      avatarName: agentName,
      avatarSrc: DOVETAIL_AGENT_AVATAR_SRC,
      avatarUserId: agentUserId,
      coAuthorName: null,
      isAgentAuthored,
    };
  }

  const authorName = getTiptapSuggestionAuthor(suggestion);

  return {
    authorName,
    avatarName: authorName,
    avatarSrc: null,
    avatarUserId: suggestion.userId,
    coAuthorName: null,
    isAgentAuthored,
  };
};

export const formatTiptapSuggestionTime = (createdAt: string) => {
  const date = parseTiptapCollaborationDate(createdAt);

  if (!date) {
    return "";
  }

  return formatTiptapCollaborationTime(date);
};
